import React, { useState } from "react";
import { Dropdown, MenuProps, Flex } from "antd";
import cls from "classnames";
import { EllipsisVertical, Pin } from "lucide-react";
import useRouteQuery from "@/hooks/useRouteQuery";
import styles from "./index.module.scss";
import LeftPanelWorkName from "@/components/LeftPanelWorkName";
import WorkDeleteModal from "@/components/WorkDeleteModal";
import useWork from "@/hooks/useWork";
import type { GenerateTask } from "@/services/api";

interface GenerateTaskItemProps {
  task: GenerateTask;
  isSelected: boolean;
  onTaskClick: () => void;
}

const GenerateTaskItem: React.FC<GenerateTaskItemProps> = ({
  task,
  isSelected,
  onTaskClick,
}) => {
  const [editor, setEditor] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { taskId = "" } = useRouteQuery();
  const { renameGenerateTask, makeGenerateTaskTop, removeGenerateTask } = useWork();

  // 菜单点击事件处理函数
  const handleMenuClick: MenuProps["onClick"] = async (e) => {
    if (e.key === "edit") {
      setEditor(true);
    } else if (e.key === "top") {
      makeGenerateTaskTop(task, task.isPinned === 1 ? false : true);
    } else if (e.key === "delete") {
      setDeleteModalVisible(true);
    }
  };

  // 菜单配置
  const menu: MenuProps = {
    items: [
      { key: "edit", label: "编辑名称" },
      { key: "top", label: task.isPinned === 1 ? "取消置顶" : "置顶" },
      { key: "delete", label: "删除", danger: true },
    ],
    onClick: handleMenuClick,
  };

  // 处理重命名保存
  const handleRenameSave = async (newName: string) => {
    await renameGenerateTask(task, newName);
  };

  return (
    <div style={{ marginBottom: "5px" }}>
      <Flex
        justify="space-between"
        className={cls({
          [styles.taskItem]: true,
          [styles.selected]: isSelected,
        })}
      >
        {/* 任务名称（支持内联编辑） */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: editor ? "0px" : "0px 6px",
            flex: 1,
            minWidth: 0,
            margin: editor ? "-8px -12px" : "0",
          }}
          onClick={onTaskClick}
          role="button"
          tabIndex={0}
          aria-label={`任务：${task.name}`}
          aria-current={isSelected ? "page" : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onTaskClick();
            }
          }}
        >
          {!editor && task.isPinned === 1 && (
            <Pin className={styles.pinIcon} />
          )}
          <LeftPanelWorkName
            workId={task.generateId}
            name={task.name}
            editor={editor}
            setEditor={setEditor}
            onSave={handleRenameSave}
          />
        </div>

        {/* 下拉菜单 */}
        {!editor && (
          <div className={styles.dropDown}>
            <Dropdown
              menu={menu}
              trigger={["click"]}
              placement="bottomLeft"
              align={{
                offset: [-55, 5],
              }}
              overlayClassName={styles.dropdownMenu}
            >
              <EllipsisVertical className={styles.workSetting} />
            </Dropdown>
          </div>
        )}
      </Flex>

      {/* 删除确认弹窗 */}
      <WorkDeleteModal
        deleteModalVisible={deleteModalVisible}
        setDeleteModalVisible={setDeleteModalVisible}
        removeWork={() => removeGenerateTask(task.generateId)}
        workId={task.generateId}
      />
    </div>
  );
};

export default GenerateTaskItem;
